import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useCart } from "@/hooks/useCart";

interface StickyAddToCartProps {
  productId: string;
  productName: string;
  productImage: string;
  price: number;
  compareAtPrice: number | null;
  inStock: boolean;
  stockCount: number;
  quantity: number;
  selectedVariants: Record<string, string>;
  variants?: { name: string; options: string[] }[];
  onQuantityChange: (q: number) => void;
}

export default function StickyAddToCart({
  productId,
  productName,
  productImage,
  price,
  compareAtPrice,
  inStock,
  stockCount,
  quantity,
  selectedVariants,
  variants,
  onQuantityChange,
}: StickyAddToCartProps) {
  const { t } = useTranslation();
  const { addItem } = useCart();
  const [visible, setVisible] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const threshold = 600;
    setVisible(currentScrollY > threshold);
    setLastScrollY(currentScrollY);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const variantSummary = variants
    ? variants.map((v) => `${v.name}: ${selectedVariants[v.name] || v.options[0]}`).join(" / ")
    : "";

  const discount = compareAtPrice
    ? Math.round(((compareAtPrice - price) / compareAtPrice) * 100)
    : 0;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-40 bg-background-50 border-t border-background-200 transition-all duration-300 ${
        visible
          ? "translate-y-0 opacity-100 pointer-events-auto"
          : "translate-y-full opacity-0 pointer-events-none"
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-8 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          {/* Product info */}
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className="w-12 h-12 rounded-lg overflow-hidden bg-background-100 flex-shrink-0 hidden sm:block">
              <img src={productImage} alt={productName} className="w-full h-full object-cover" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground-900 truncate">{productName}</p>
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-bold text-foreground-950">${price.toFixed(2)}</span>
                {compareAtPrice && (
                  <span className="text-xs text-foreground-400 line-through">${compareAtPrice.toFixed(2)}</span>
                )}
                {discount > 0 && (
                  <span className="text-xs font-semibold text-primary-600 whitespace-nowrap">-{discount}%</span>
                )}
              </div>
              {variantSummary && (
                <p className="text-xs text-foreground-500 truncate mt-0.5">{variantSummary}</p>
              )}
            </div>
          </div>

          {/* Quantity + CTA */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="hidden sm:flex items-center border border-background-200 rounded-lg">
              <button
                onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
                className="w-9 h-9 flex items-center justify-center text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer rounded-l-lg"
                disabled={!inStock}
              >
                <i className="ri-subtract-line text-sm"></i>
              </button>
              <span className="w-10 h-9 flex items-center justify-center text-sm font-medium text-foreground-900 border-x border-background-200">
                {quantity}
              </span>
              <button
                onClick={() => onQuantityChange(Math.min(stockCount, quantity + 1))}
                className="w-9 h-9 flex items-center justify-center text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer rounded-r-lg"
                disabled={!inStock}
              >
                <i className="ri-add-line text-sm"></i>
              </button>
            </div>

            <button
              onClick={() => {
                if (!inStock) return;
                addItem({
                  productId,
                  nameEn: productName,
                  nameAr: productName,
                  slug: "",
                  price,
                  imageUrl: productImage,
                  quantity,
                  maxStock: stockCount,
                  selectedVariants,
                });
              }}
              disabled={!inStock}
              className="flex items-center justify-center gap-2 px-6 py-2.5 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-shopping-bag-line"></i>
              {inStock ? t("product.add_to_cart") : t("product.out_of_stock")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}